import { Centrifuge } from 'centrifuge'

import { TypeID } from '@trustycars/genie-ui/types'

import { AuthAPI } from '$services/api/auth'
import { successResolver } from '$services/api/common'
import { EventName, Payloads } from '$services/rtc/auth'

import { baseURL } from '../constants'

export * from './types'

type CreateAuthRTCOptions = {
  user_id: TypeID
  token_id: TypeID
  authAPI: AuthAPI
}

const getConnectionToken = async (authAPI: AuthAPI) => {
  const {
    data: { token }
  } = await authAPI.rtcConnectionToken().then(successResolver)

  return token
}

const getSubscriptionToken = async (
  authAPI: AuthAPI,
  ...subParams: Parameters<typeof authAPI.rtcSubscriptionToken>
) => {
  const {
    data: { token }
  } = await authAPI.rtcSubscriptionToken(...subParams).then(successResolver)

  return token
}

export type AuthRTC = ReturnType<typeof createAuthRTC>
export const createAuthRTC = ({
  authAPI,
  token_id,
  user_id
}: CreateAuthRTCOptions) => {
  const connection = new Centrifuge(baseURL, {
    getToken: () => getConnectionToken(authAPI)
  })

  const privateChannelName = `sso-ws:${user_id}.${token_id}`
  const privateChannel = connection.newSubscription(privateChannelName, {
    getToken: () =>
      getSubscriptionToken(authAPI, { channel: privateChannelName }),
    recoverable: true
  })

  connection.connect()

  const close = () => {
    privateChannel.removeAllListeners()
    privateChannel.unsubscribe()
    connection.disconnect()
  }

  return { connection, privateChannel, close }
}

export const subscribeRtcEvent = <EN extends EventName>(
  event_name: EN,
  options: {
    rtc: ReturnType<typeof createAuthRTC>
    action: (payload: Payloads[EN]) => void
    validate?: (payload: Payloads[EN]) => boolean
  }
) => {
  const {
    action,
    validate,
    rtc: { privateChannel }
  } = options

  privateChannel.on('publication', ({ data }) => {
    const hasType = 'event_name' in data

    if (!hasType) return

    if (event_name !== data.event_name) return

    if (validate && !validate(data)) return

    action(data)
  })

  privateChannel.subscribe()

  return () => {
    privateChannel.removeAllListeners()
    privateChannel.unsubscribe()
  }
}
